import React, { Suspense } from 'react';
import dynamic from 'next/dynamic';

import { SOCIAL_CARD_TYPE, socialCardKeys } from '@/src/constants/socialcard.constant';

import { RichText, MediaImage, StudioWrap } from './StudioCardKit';
import { StudioPicture, StudioAudio } from './StudioMedia';

/**
 * The video player is the set's only hls.js consumer, so it is code-split:
 * an embed on an older widget type, or a studio wall with no video on screen,
 * never downloads the HLS runtime (~169 kB). Do not make this a static import.
 */
const StudioVideo = dynamic(() => import('./StudioVideo'), { suspense: true });

/** Holds the row height while the player chunk arrives. */
const VideoFallback = () => (
  <div style={{ width: '100%', paddingTop: '75%', borderRadius: 10, backgroundColor: '#000' }} />
);

const LazyVideo = (props) => (
  <Suspense fallback={<VideoFallback />}>
    <StudioVideo {...props} />
  </Suspense>
);

/**
 * The body of a studio card: whatever this testimonial actually is.
 *
 * Text and imported reviews render the message (with highlight + link
 * formatting and the Read More clamp), video and audio render their player
 * with the stored summary quoted directly underneath, and picture
 * testimonials render the image.
 *
 * Video and audio are the SAME in all ten types, and the same as on the older
 * widget types. They take no per-card styling - don't add any.
 *
 * Media is detected from the DATA first and the card type second. A video or
 * audio testimonial carries no message_content, so if we trusted the type
 * alone and it didn't match (a string "9", a type we don't know about) the
 * card would fall through to the text branch and render an empty body - i.e.
 * the testimonial would look like it had vanished.
 *
 * Social-platform videos (YouTube, Loom, ...) are handled one level up in each
 * Get*Card dispatcher, which hands them to the existing SocialVideoCard.
 */

/* eslint-disable-next-line eqeqeq */
const sameType = (a, b) => a == b; // tolerate "9" as well as 9

/* NB: video_summary is NOT a video signal - audio testimonials store their
   transcript in the same field. Only the playback id is proof of video. */
export const hasVideo = (cardItem, socialCardType) =>
  sameType(socialCardType, SOCIAL_CARD_TYPE.VIDEO) ||
  !!(cardItem && cardItem[socialCardKeys.videoPlaybackId]);

export const hasAudio = (cardItem, socialCardType) =>
  sameType(socialCardType, SOCIAL_CARD_TYPE.AUDIO) || !!(cardItem && cardItem[socialCardKeys.audioAssetUrl]);

export const hasPicture = (cardItem, socialCardType) =>
  sameType(socialCardType, SOCIAL_CARD_TYPE.PICTURE);

/** Video and audio testimonials replace the card rather than sit inside it. */
export const isMediaTestimonial = (cardItem, socialCardType) =>
  hasVideo(cardItem, socialCardType) || hasAudio(cardItem, socialCardType);

/**
 * A video or audio testimonial, rendered as the WHOLE card.
 *
 * Every studio card returns this instead of its own layout when the
 * testimonial is media, because the player already carries the person's name
 * and work title - a header, avatar, meta rail and card border around it would
 * repeat all of that and box the video in. The card's design survives in one
 * place only: `enclosure`, the panel the summary extends into (and nothing is
 * drawn at all when there is no summary).
 */
export const StudioMediaCard = ({ t, cardItem, socialCardType, enclosure, summaryStyle }) => (
  <StudioWrap>
    {hasVideo(cardItem, socialCardType) ? (
      <LazyVideo t={t} cardItem={cardItem} enclosure={enclosure} summaryStyle={summaryStyle} />
    ) : (
      <StudioAudio t={t} cardItem={cardItem} enclosure={enclosure} summaryStyle={summaryStyle} />
    )}
  </StudioWrap>
);

export const StudioBody = ({
  t,
  cardItem,
  socialCardType,
  textStyle,
  textClassName,
  quoteLength = 300,
  mediaRadius = 10,
  objectFit = 'cover',
  showMediaImage = true,
  gap = 12,
}) => {
  const stack = { display: 'flex', flexDirection: 'column', gap, minWidth: 0 };

  /* Video and audio render themselves: player, then the summary directly
     under it. They take no styling from the host card and nothing wraps them
     - see StudioMedia. Don't add a frame, border or container here. */
  if (hasVideo(cardItem, socialCardType)) {
    return <LazyVideo t={t} cardItem={cardItem} summaryStyle={textStyle} objectFit={objectFit} />;
  }

  if (hasAudio(cardItem, socialCardType)) {
    return <StudioAudio t={t} cardItem={cardItem} summaryStyle={textStyle} />;
  }

  if (hasPicture(cardItem, socialCardType)) {
    return (
      <div style={stack}>
        <StudioPicture t={t} cardItem={cardItem} radius={mediaRadius} />
        <RichText t={t} cardItem={cardItem} style={textStyle} className={textClassName} length={quoteLength} />
      </div>
    );
  }

  return (
    <div style={stack}>
      <RichText t={t} cardItem={cardItem} style={textStyle} className={textClassName} length={quoteLength} />
      {showMediaImage && cardItem && cardItem[socialCardKeys.messageMediaImage] ? (
        <MediaImage t={t} cardItem={cardItem} radius={mediaRadius} />
      ) : null}
    </div>
  );
};

export default StudioBody;
